import { type ComponentProps, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, useId } from "react";

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

/** Label, control, hint, and error, wired together for screen readers. */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint === undefined ? "" : hintId, error === undefined ? "" : errorId].join(" ").trim();
  return (
    <div className={["field", className ?? ""].join(" ").trim()}>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children({ id, describedBy: describedBy === "" ? undefined : describedBy, invalid: error !== undefined })}
      {hint === undefined ? null : (
        <div className="field-hint" id={hintId}>
          {hint}
        </div>
      )}
      {error === undefined ? null : (
        <div className="field-error" id={errorId} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={["input", className ?? ""].join(" ").trim()} {...rest} />;
}

export function Textarea({ className, ...rest }: ComponentProps<"textarea">) {
  return <textarea className={["input", "textarea", className ?? ""].join(" ").trim()} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="select-wrap">
      <select className={["input", "select", className ?? ""].join(" ").trim()} {...rest}>
        {children}
      </select>
    </div>
  );
}
